import React from 'react';
import MenuList from '@wix/da-ds/pkg/Dropdown/MenuList';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Flex from '@wix/da-ds/pkg/Flex';
import { Url, URLS } from '@wix/da-url';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData, TopNavMenuClickBiEvent } from '@wix/da-bi/pkg/events';
import { getVerificationEmailAddress } from '../../../../../../publicSession/selectors';
import UserLink from '../../../../../../User/Link/UserLinkWithTooltip';
import VerificationReminder from '../../../../VerificationReminder';
import { useCurrentUser } from '../../../../../../utils/hooks/useCurrentUser';
import {
  useContentFilterItem,
  useLogoutItem,
  useSaleBannerItem,
  useBuyCoreItem,
  useStudioItem,
  useThemeSwitcherItem,
  useAISuppressionItem,
  useHelpItem,
  useSettingsItem,
  usePointsBalanceItem,
  useCustomerDashboardItem,
  useEarningsItem,
  useBillingItem,
  usePushSubscriptionItem,
} from '../../../items';

import userMenuStyles from '../../UserMenuDesktop.scss';
import SiteHeaderStyles from '../../../../../SiteHeaderDesktop/SiteHeaderDesktop.scss';

interface Props {
  className?: string;
  disableThemeSwitcher?: boolean;
}

const LoggedInMenu: React.FC<Props> = ({ disableThemeSwitcher, className }) => {
  const { t } = useTranslation();
  const verificationEmail = useSelector(getVerificationEmailAddress);

  const currentUser = useCurrentUser();
  const buyCoreItem = useBuyCoreItem();
  const saleBannerItem = useSaleBannerItem();
  const logoutItem = useLogoutItem();
  const contentFilterItem = useContentFilterItem();
  const studioItem = useStudioItem();
  const themeSwitcherItem = useThemeSwitcherItem();
  const helpItem = useHelpItem();
  const aiSuppressionItem = useAISuppressionItem();
  const settingsItem = useSettingsItem();
  const pointsBalanceItem = usePointsBalanceItem();
  const customerDashboardItem = useCustomerDashboardItem();
  const earningsItem = useEarningsItem();
  const billingItem = useBillingItem();
  const pushSubscriptionItem = usePushSubscriptionItem();

  const loggedInUser = currentUser!;

  return (
    <BiLoggerContextProvider
      value={{
        sectionname: 'menu',
      }}
    >
      <MenuList
        id="site-header-user-menu"
        className={classnames(
          className,
          userMenuStyles['user-menu'],
          SiteHeaderStyles['icon-submenu']
        )}
        renderOnSSR
        itemsFillContainerWidth
        items={[
          {
            items: [
              {
                className: userMenuStyles['user-profile-line-root'],
                label: (
                  <Flex
                    alignItems="baseline"
                    justifyContent="space-between"
                    className={userMenuStyles['user-profile-line']}
                  >
                    <UserLink
                      className={userMenuStyles['user-link']}
                      symbolClassName={userMenuStyles['user-link-symbol']}
                      user={loggedInUser}
                      withTooltip={false}
                    />
                  </Flex>
                ),
              },
              buyCoreItem,
              saleBannerItem,
            ],
          },
          {
            items: [
              !!verificationEmail && {
                label: <VerificationReminder email={verificationEmail} />,
                className: userMenuStyles['verification-item'],
              },
              !verificationEmail && studioItem,
              !verificationEmail && {
                label: t('siteHeader.userMenu.myStash'),
                link: Url.buildFromPath(URLS.STASH),
                biData: BiData<TopNavMenuClickBiEvent>({
                  evid: 702,
                  link_url: Url.buildFromPath(URLS.STASH),
                  menu_item: 'stash',
                  menu_name: 'profile',
                }),
              },
              !verificationEmail && {
                label: t('siteHeader.userMenu.dreamup'),
                link: Url.dreamupLink(),
                biData: BiData<TopNavMenuClickBiEvent>({
                  evid: 702,
                  link_url: Url.dreamupLink(),
                  menu_item: 'dreamup',
                  menu_name: 'profile',
                }),
              },
              !verificationEmail && pointsBalanceItem,
              !verificationEmail && earningsItem,
              billingItem,
              !verificationEmail && customerDashboardItem,
            ].filter(Boolean),
          },
          {
            items: [
              !disableThemeSwitcher && themeSwitcherItem,
              contentFilterItem,
              aiSuppressionItem,
            ].filter(Boolean),
          },
          {
            items: [
              settingsItem,
              pushSubscriptionItem,
              helpItem,
              logoutItem,
            ].filter(Boolean),
            className:
              verificationEmail &&
              userMenuStyles['post-verification-group'],
          },
        ]}
      />
    </BiLoggerContextProvider>
  );
};

LoggedInMenu.displayName = 'LoggedInMenu';

export default LoggedInMenu;
